import React, { useContext, useMemo } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { Button, ButtonType } from '@/components/atoms/button'
import { Option, Select } from '@/components/atoms/select'
import { AddFormFieldButton } from '@/components/molecules/add-form-field-button'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { LoaderContextCreator } from '@/contexts/loader'
import { useErrorHandler } from '@/hooks/use-error-handler'
import * as api from '@/apis/aipass'
import type { DiscountARRule, DiscountARRuleInputParams } from '@/models/discount-ar-rule'
import type { DiscountARRuleMaster } from '@/components/pages/setting/sales-manager/accounts-receivable-display'
import { useDiscountReceivableTargetChangeConfirmDialog } from '../use-discount-receivable-target-change-confirm-dialog'

type Props = {
  detail: DiscountARRule | null
  master: DiscountARRuleMaster
  existingRules: DiscountARRule[]
  onCancel: () => void
  onSave: () => void
}

export const SettingDiscountARRuleDetailModal: React.FC<Props> = ({ detail, master, existingRules, onCancel, onSave }) => {
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const { openConfirmDialog, DiscountReceivableTargetChangeConfirmDialog } = useDiscountReceivableTargetChangeConfirmDialog()
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<DiscountARRuleInputParams>({
    mode: 'all',
    defaultValues: {
      discountSubjectCode: detail?.discountSubjectCode || '',
      details: detail?.details.map(item => ({ arCode: item.arCode, targetFlag: item.targetFlag })) || [],
    },
  })
  const detailsFieldArray = useFieldArray({
    control,
    name: 'details',
    rules: { required: true, minLength: 1 },
  })
  const watchedDetails = useWatch({ control, name: 'details' }) || []

  const usedDiscountSubjectCodes = useMemo(() => {
    return new Set(existingRules.filter(rule => rule.id !== detail?.id).map(rule => rule.discountSubjectCode))
  }, [existingRules, detail?.id])

  const discountOptions = useMemo(() => {
    const options = master.discounts
      .filter(
        discount =>
          !usedDiscountSubjectCodes.has(discount.discountSubjectCode) || discount.discountSubjectCode === detail?.discountSubjectCode,
      )
      .map(discount => ({ value: discount.discountSubjectCode, label: discount.name, disabled: false }))
    if (detail?.discountSubjectCode && !options.some(option => option.value === detail.discountSubjectCode)) {
      options.unshift({ value: detail.discountSubjectCode, label: detail.discountName, disabled: true })
    }
    return options
  }, [detail, master.discounts, usedDiscountSubjectCodes])

  const accountsReceivableOptions = useMemo(() => {
    const options = master.accountsReceivables.map(accountsReceivable => ({
      value: accountsReceivable.code,
      label: accountsReceivable.name,
      disabled: false,
    }))
    detail?.details.forEach(item => {
      if (!options.some(option => option.value === item.arCode)) {
        options.push({ value: item.arCode, label: item.arName, disabled: true })
      }
    })
    return options
  }, [detail, master.accountsReceivables])

  const currentSelectedArCodes = useMemo(() => {
    return watchedDetails.map(item => item?.arCode).filter((arCode): arCode is string => !!arCode)
  }, [watchedDetails])

  const canAddAccountsReceivable = useMemo(() => {
    const selectableCodes = accountsReceivableOptions.filter(option => !option.disabled).map(option => option.value)
    return selectableCodes.some(code => !currentSelectedArCodes.includes(code))
  }, [accountsReceivableOptions, currentSelectedArCodes])

  const buildAccountsReceivableOptions = (index: number, currentValue?: string) => {
    const selectedByOtherRows = new Set(
      watchedDetails
        .map((item, rowIndex) => (rowIndex === index ? undefined : item?.arCode))
        .filter((arCode): arCode is string => !!arCode),
    )
    return accountsReceivableOptions.filter(option => option.value === currentValue || !selectedByOtherRows.has(option.value))
  }

  const submit = async (value: DiscountARRuleInputParams) => {
    const isConfirmed = await openConfirmDialog()
    if (!isConfirmed) {
      return
    }

    try {
      setIsLoading(true)
      if (detail) {
        await api.updateSalesDiscountARRules(detail.id, value)
      } else {
        await api.storeSalesDiscountARRules(value)
      }
      onSave()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal customCss={modalStyle} containerCss={css({ pointerEvents: 'none' })}>
        <ModalHeader>{t(detail ? 'Edit' : 'Add new')}</ModalHeader>
        <ModalBody>
          <div css={bodyStyle}>
            <div>
              <div css={fieldLabelStyle}>{t('Discount')}</div>
              <Controller
                control={control}
                name="discountSubjectCode"
                rules={{ required: t('Required field has not been entered') }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value || undefined}
                    onChange={onChange}
                    error={errors.discountSubjectCode?.message}
                    placeholder={t('Please select')}
                  >
                    {discountOptions.map(option => (
                      <Option key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </div>

            <div>
              <div css={fieldLabelStyle}>{t('Accounts receivable')}</div>
              <div css={detailListStyle}>
                {detailsFieldArray.fields.map((field, index) => (
                  <div key={field.id} css={detailItemStyle}>
                    <Controller
                      control={control}
                      name={`details.${index}.arCode`}
                      rules={{ required: t('Required field has not been entered') }}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          value={value || undefined}
                          onChange={onChange}
                          error={errors.details?.[index]?.arCode?.message}
                          placeholder={t('Please select')}
                          customStyle={css({ flex: 1, paddingBottom: 0 })}
                        >
                          {buildAccountsReceivableOptions(index, value).map(option => (
                            <Option key={option.value} value={option.value} disabled={option.disabled}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`details.${index}.targetFlag`}
                      render={({ field: { onChange, value } }) => (
                        <div css={toggleValueStyle}>
                          <ToggleButton value={value} onChange={() => onChange(!value)} label={{ on: t('Target'), off: t('Non target') }} />
                        </div>
                      )}
                    />
                    <DeleteIcon onClick={() => detailsFieldArray.remove(index)} />
                  </div>
                ))}

                {errors.details?.root && <div css={errorTextStyle}>{t('Required field has not been entered')}</div>}

                {canAddAccountsReceivable && (
                  <AddFormFieldButton
                    onAdd={() => detailsFieldArray.append({ arCode: '', targetFlag: false })}
                    label={t('Add accounts receivable')}
                  />
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button buttonType={3} height="38px" width="110px" marginRight={16} onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button
            buttonType={isValid ? ButtonType.Primary : ButtonType.PrimaryDisabled}
            height="38px"
            width="110px"
            onClick={handleSubmit(submit)}
          >
            {t('Save')}
          </Button>
        </ModalFooter>
      </Modal>
      <DiscountReceivableTargetChangeConfirmDialog />
    </>
  )
}

const modalStyle = css({
  maxHeight: 720,
  height: 'auto',
  width: 720,
  pointerEvents: 'auto',
})

const bodyStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
})

const fieldLabelStyle = css({
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
  letterSpacing: '0.6px',
  marginBottom: 12,
})

const toggleValueStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  span: {
    minWidth: 72,
    textAlign: 'right',
    fontSize: 12,
    fontWeight: 'bold',
    color: '#676767',
  },
})

const detailListStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
})

const detailItemStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  padding: '16px 16px 12px',
  border: '1px solid #CCCCCC',
  borderRadius: 10,
  background: '#FFF',
})

const errorTextStyle = css({
  fontSize: 10,
  color: 'rgba(255,0,0,0.5)',
  marginTop: 4,
})
